// Dynamic token image resolver
// Tries multiple sources: local images, DexScreener, Monadvision

const LOCAL_IMAGES: Record<string, string> = {
    DUCK: '/images/duck.png',
};

/**
 * Get the best available image URL for a token.
 * Priority: local -> DexScreener -> Monadvision -> gradient fallback
 */
export function getTokenImageUrl(symbol: string, address: string): string {
    // Check local images first
    if (LOCAL_IMAGES[symbol.toUpperCase()]) {
        return LOCAL_IMAGES[symbol.toUpperCase()];
    }

    // DexScreener token image (works for most listed tokens)
    return `https://dd.dexscreener.com/ds-data/tokens/monad/${address.toLowerCase()}.png`;
}

/**
 * Get Monadvision explorer link for a token
 */
export function getMonadvisionTokenUrl(address: string): string {
    return `https://monadvision.com/token/${address}`;
}

/**
 * Get Monadscan explorer link for a token
 */
export function getMonadscanTokenUrl(address: string): string {
    return `https://testnet.monadexplorer.com/token/${address}`;
}

/**
 * Fetch token info from DexScreener API
 */
export async function fetchTokenInfo(address: string): Promise<{
    symbol: string;
    name: string;
    imageUrl: string;
    priceUsd: number;
    volume24h: number;
    liquidity: number;
    marketCap: number;
} | null> {
    try {
        const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${address}`, {
            signal: AbortSignal.timeout(8000),
        });
        if (!res.ok) return null;
        const data = await res.json();

        if (!data.pairs || data.pairs.length === 0) return null;

        // Pick best pair (highest liquidity on Monad, fallback to any chain)
        const monadPairs = data.pairs.filter((p: any) => p.chainId === 'monad');
        const pair = monadPairs.length > 0
            ? monadPairs.sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0]
            : data.pairs.sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];

        if (!pair) return null;

        // Determine if our address is the base or quote token
        const isBase = pair.baseToken?.address?.toLowerCase() === address.toLowerCase();
        const token = isBase ? pair.baseToken : pair.quoteToken;

        return {
            symbol: token?.symbol || 'UNKNOWN',
            name: token?.name || 'Unknown Token',
            imageUrl: getTokenImageUrl(token?.symbol || '', address),
            priceUsd: parseFloat(pair.priceUsd || '0'),
            volume24h: pair.volume?.h24 || 0,
            liquidity: pair.liquidity?.usd || 0,
            marketCap: pair.marketCap || 0,
        };
    } catch {
        return null;
    }
}
